/**
 * Judge Training Clip 4 — How Scoring Works
 * Explains the two-stage scoring model:
 *   1. Each attempt: 3 judges score → averaged into attempt score
 *   2. Best attempt score = rider's final score (max model)
 * Shows an animated example with riders and a leaderboard.
 * 720 frames at 30fps = 24 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { FadeTransition } from "../components/FadeTransition";

export function JudgeCourse4() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={65}><Title /></Sequence>
      <Sequence from={55} durationInFrames={180}><StageOne /></Sequence>
      <Sequence from={225} durationInFrames={180}><StageTwo /></Sequence>
      <Sequence from={395} durationInFrames={255}>
        <FadeTransition durationInFrames={255} fadeIn={10} fadeOut={10}><Leaderboard /></FadeTransition>
      </Sequence>
      <Sequence from={640} durationInFrames={80}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 48, 65], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 3, fontFamily: mono, marginBottom: 14 }}>Judge Training · Clip 4</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>How Scoring Works</div>
    </AbsoluteFill>
  );
}

/* ── Stage 1: Judges score an attempt → averaged ── */

function StageOne() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 155, 180], [0, 1, 1, 0], { extrapolateRight: "clamp" });

  const judgeScores = [7.0, 8.5, 6.5];
  const avg = 7.3;

  const j1op = interpolate(frame, [15, 30], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const j2op = interpolate(frame, [35, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const j3op = interpolate(frame, [55, 70], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const arrowOp = interpolate(frame, [85, 100], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const avgOp = interpolate(frame, [100, 115], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      {/* Section label */}
      <div style={{ position: "absolute", top: 120, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ fontSize: 10, color: "#3b82f6", textTransform: "uppercase", letterSpacing: 3, fontFamily: mono, marginBottom: 8 }}>Step 1</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Each attempt is scored by all judges</div>
        <div style={{ fontSize: 14, color: "#71717a", marginTop: 8 }}>Their scores are <span style={{ color: "#3b82f6", fontWeight: 700 }}>averaged</span> to get the attempt score</div>
      </div>

      {/* Visual: Judge boxes → arrow → average */}
      <div style={{ display: "flex", alignItems: "center", gap: 40, marginTop: 40 }}>
        {judgeScores.map((score, i) => (
          <div key={i} style={{ opacity: [j1op, j2op, j3op][i], textAlign: "center" }}>
            <div style={{ fontSize: 10, color: "#71717a", fontFamily: mono, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Judge {i + 1}</div>
            <div style={{
              width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center",
              background: "#111113", border: "1px solid #2a2a32", borderRadius: 8,
              fontSize: 32, fontWeight: 800, color: "#e4e4e7", fontFamily: mono,
            }}>
              {score.toFixed(1)}
            </div>
          </div>
        ))}

        <div style={{ opacity: arrowOp, fontSize: 32, color: "#3b82f6", fontWeight: 800 }}>→</div>

        <div style={{ opacity: avgOp, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#3b82f6", fontFamily: mono, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Attempt Score</div>
          <div style={{
            width: 120, height: 100, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(59,130,246,0.1)", border: "2px solid #3b82f6", borderRadius: 8,
            fontSize: 36, fontWeight: 800, color: "#3b82f6", fontFamily: mono,
          }}>
            {avg.toFixed(1)}
          </div>
          <div style={{ fontSize: 11, color: "#52525b", fontFamily: mono, marginTop: 6 }}>(7.0 + 8.5 + 6.5) / 3</div>
        </div>
      </div>
    </AbsoluteFill>
  );
}

/* ── Stage 2: Best attempt = final score ── */

function StageTwo() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 155, 180], [0, 1, 1, 0], { extrapolateRight: "clamp" });

  const attempts = [
    { num: 1, score: 7.3 },
    { num: 2, score: 8.2 },
    { num: 3, score: 6.0 },
  ];
  const best = 1; // index of best attempt (8.2)

  const a1op = interpolate(frame, [20, 35], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const a2op = interpolate(frame, [40, 55], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const a3op = interpolate(frame, [60, 75], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const highlightOp = interpolate(frame, [90, 105], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const arrowOp = interpolate(frame, [105, 120], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const finalOp = interpolate(frame, [120, 135], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ position: "absolute", top: 120, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ fontSize: 10, color: "#22c55e", textTransform: "uppercase", letterSpacing: 3, fontFamily: mono, marginBottom: 8 }}>Step 2</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Best attempt wins</div>
        <div style={{ fontSize: 14, color: "#71717a", marginTop: 8 }}>The rider&apos;s final score is their <span style={{ color: "#22c55e", fontWeight: 700 }}>highest</span> attempt score</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 30, marginTop: 40 }}>
        {attempts.map((a, i) => {
          const aop = [a1op, a2op, a3op][i];
          const isBest = i === best;
          const glow = isBest ? highlightOp : 0;
          return (
            <div key={a.num} style={{ opacity: aop, textAlign: "center" }}>
              <div style={{ fontSize: 10, color: "#71717a", fontFamily: mono, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Attempt {a.num}</div>
              <div style={{
                width: 100, height: 100, display: "flex", alignItems: "center", justifyContent: "center",
                background: isBest ? interpolateColor(glow, "rgba(34,197,94,0.05)", "rgba(34,197,94,0.15)") : "#111113",
                border: `${isBest && glow > 0 ? 2 : 1}px solid ${isBest && glow > 0 ? "#22c55e" : "#2a2a32"}`,
                borderRadius: 8,
                fontSize: 32, fontWeight: 800,
                color: isBest && glow > 0 ? "#22c55e" : "#e4e4e7",
                fontFamily: mono,
                boxShadow: isBest && glow > 0 ? "0 0 20px rgba(34,197,94,0.2)" : "none",
                transition: "all 0.3s",
              }}>
                {a.score.toFixed(1)}
              </div>
            </div>
          );
        })}

        <div style={{ opacity: arrowOp, fontSize: 32, color: "#22c55e", fontWeight: 800 }}>→</div>

        <div style={{ opacity: finalOp, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#22c55e", fontFamily: mono, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Final Score</div>
          <div style={{
            width: 120, height: 100, display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(34,197,94,0.1)", border: "2px solid #22c55e", borderRadius: 8,
            fontSize: 36, fontWeight: 800, color: "#22c55e", fontFamily: mono,
          }}>
            8.2
          </div>
          <div style={{ fontSize: 11, color: "#52525b", fontFamily: mono, marginTop: 6 }}>max(7.3, 8.2, 6.0)</div>
        </div>
      </div>
    </AbsoluteFill>
  );
}

/* ── Animated leaderboard example ── */

const RIDERS = [
  { name: "Mikayo Mundy", nick: "Kayo", att: [7.0, 9.1, 8.3], final: 9.1, best: 1 },
  { name: "Takoda", nick: "Taka", att: [7.3, 8.2, 6.0], final: 8.2, best: 1 },
  { name: "Bentley Bland", nick: "Big B", att: [7.8, 5.5, 7.2], final: 7.8, best: 0 },
];

function Leaderboard() {
  const frame = useCurrentFrame();
  const headerOp = interpolate(frame, [15, 25], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
      <div style={{ position: "absolute", top: 100, left: 0, right: 0, textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Example Leaderboard</div>
        <div style={{ fontSize: 14, color: "#71717a", marginTop: 8 }}>Best attempt score determines placement</div>
      </div>

      <div style={{
        width: 800, background: "#111113", border: "1px solid #2a2a32", borderRadius: 8, overflow: "hidden",
        marginTop: 40, opacity: headerOp,
      }}>
        {/* Header */}
        <div style={{
          display: "grid", gridTemplateColumns: "40px 180px 100px 100px 100px 100px",
          background: "#0a0a0b", borderBottom: "1px solid #2a2a32", padding: "10px 16px",
        }}>
          {["#", "Rider", "Att 1", "Att 2", "Att 3", "Final"].map((h) => (
            <div key={h} style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#52525b", fontFamily: mono, textAlign: h === "Rider" ? "left" : "center" }}>
              {h}
            </div>
          ))}
        </div>

        {/* Rows */}
        {RIDERS.map((r, i) => {
          const rowDelay = 40 + i * 25;
          const rowOp = interpolate(frame, [rowDelay, rowDelay + 15], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const highlightDelay = 130 + i * 20;
          const hlOp = interpolate(frame, [highlightDelay, highlightDelay + 15], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

          return (
            <div key={r.name} style={{
              display: "grid", gridTemplateColumns: "40px 180px 100px 100px 100px 100px",
              padding: "10px 16px", borderBottom: i < RIDERS.length - 1 ? "1px solid #1a1a1e" : "none",
              background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
              opacity: rowOp,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: i === 0 ? "#eab308" : "#71717a", textAlign: "center", fontFamily: mono }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#e4e4e7" }}>{r.name}</div>
                <div style={{ fontSize: 10, color: "#52525b" }}>{r.nick}</div>
              </div>
              {r.att.map((score, j) => {
                const isBest = j === r.best;
                return (
                  <div key={j} style={{
                    fontSize: 14, fontWeight: isBest && hlOp > 0 ? 800 : 500, textAlign: "center", fontFamily: mono,
                    color: isBest && hlOp > 0 ? "#3b82f6" : "#a1a1aa",
                  }}>
                    {score.toFixed(1)}
                  </div>
                );
              })}
              <div style={{
                fontSize: 18, fontWeight: 800, textAlign: "center", fontFamily: mono,
                color: i === 0 ? "#eab308" : "#22c55e",
              }}>
                {r.final.toFixed(1)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Annotation */}
      {frame > 175 && (
        <div style={{
          position: "absolute", bottom: 160, left: 0, right: 0, textAlign: "center",
          opacity: interpolate(frame, [175, 190], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
        }}>
          <div style={{
            display: "inline-block", padding: "10px 24px",
            background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 6,
            fontSize: 13, color: "#93c5fd", fontFamily: mono,
          }}>
            Highlighted attempt = the one that counts as their final score
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Score each attempt fairly — the best one counts</div>
    </AbsoluteFill>
  );
}

/* ── Helpers ── */

function interpolateColor(t: number, from: string, to: string): string {
  return t > 0.5 ? to : from;
}

const mono = "'JetBrains Mono', monospace";
